import type {
  LalKitabKundliResponse,
  PlanetEffect,
  ActiveRemedy,
  DetectedDebt,
} from "@/lib/api/lal-kitab";
import type { ReactNode } from "react";
import { OVERALL_LABEL, OVERALL_COLOR } from "./_constants";

type Language = "en" | "hi";

const COPY = {
  en: {
    assessment: "Overall Assessment",
    good: "Good",
    needsUpay: "Need Upay",
    activeUpay: "Active Upay",
    planetPlacements: "Planet Placements (Lal Kitab)",
    planet: "Planet",
    house: "House",
    effect: "Effect",
    comfortable: "Comfortable",
    remedies: "Active Upay (Remedies)",
    remediesBody:
      "These remedies apply to planets placed in houses that Lal Kitab marks as uncomfortable for them. Performing these upay regularly — especially on the specified day — is the core Lal Kitab prescription.",
    debts: "Ancestral Debts (Rin / ऋण)",
    debtsBody:
      "Lal Kitab identifies specific planet-house combinations as indicators of karmic debts. Clearing these through the prescribed upay is considered essential for removing blockages in career, family, and health.",
    donate: "Donate",
    feed: "Feed",
    gemstone: "Gemstone",
    remedy: "Remedy",
  },
  hi: {
    assessment: "समग्र आकलन",
    good: "शुभ",
    needsUpay: "उपाय चाहिए",
    activeUpay: "सक्रिय उपाय",
    planetPlacements: "ग्रह स्थिति (लाल किताब)",
    planet: "ग्रह",
    house: "भाव",
    effect: "प्रभाव",
    comfortable: "अनुकूल",
    remedies: "सक्रिय उपाय",
    remediesBody:
      "ये उपाय उन ग्रहों के लिए हैं जिन्हें लाल किताब उनकी भाव स्थिति में असहज मानती है। इन्हें नियमित रूप से, विशेषकर बताए गए दिन पर करना, लाल किताब की मूल पद्धति है।",
    debts: "पैतृक ऋण (Rin / ऋण)",
    debtsBody:
      "लाल किताब कुछ ग्रह-भाव योगों को कर्म ऋण का संकेत मानती है। बताए गए उपाय करियर, परिवार और स्वास्थ्य की रुकावटों को समझने और कम करने के लिए दिए जाते हैं।",
    donate: "दान",
    feed: "खिलाएं",
    gemstone: "रत्न",
    remedy: "उपाय",
  },
} as const;

const HINDI_OVERALL_LABEL: Record<string, string> = {
  mostly_positive: "अधिकतर शुभ",
  mixed: "मिश्रित",
  challenging: "चुनौतीपूर्ण",
};

function EffectBadge({ effect, language }: { effect: "good" | "bad"; language: Language }) {
  const copy = COPY[language];
  return (
    <span
      className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{
        background: effect === "good" ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.12)",
        color: effect === "good" ? "var(--color-success)" : "var(--color-error)",
      }}
    >
      {effect === "good" ? copy.comfortable : copy.needsUpay}
    </span>
  );
}

export function PlanetEffectsTable({
  effects,
  language,
}: {
  effects: PlanetEffect[];
  language: Language;
}) {
  const copy = COPY[language];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr style={{ borderBottom: "1px solid var(--ish-border)" }}>
            <th
              className="text-left py-2 pr-4 font-semibold"
              style={{ color: "var(--ish-text-muted)" }}
            >
              {copy.planet}
            </th>
            <th
              className="text-center py-2 pr-4 font-semibold"
              style={{ color: "var(--ish-text-muted)" }}
            >
              {copy.house}
            </th>
            <th className="text-left py-2 font-semibold" style={{ color: "var(--ish-text-muted)" }}>
              {copy.effect}
            </th>
          </tr>
        </thead>
        <tbody>
          {effects.map((e) => (
            <tr
              key={e.planet}
              style={{ borderBottom: "1px solid var(--color-border-light, #f0ebe3)" }}
            >
              <td className="py-2 pr-4" style={{ color: "var(--ish-dark)" }}>
                <span className="font-medium">
                  {language === "hi" ? e.planet_display_hi : e.planet_display_en}
                </span>
                {language === "en" ? (
                  <span className="ml-2 text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
                    {e.planet_display_hi}
                  </span>
                ) : null}
              </td>
              <td
                className="py-2 pr-4 text-center font-semibold"
                style={{ color: "var(--ish-accent)" }}
              >
                {e.house}
              </td>
              <td className="py-2">
                <EffectBadge effect={e.effect as "good" | "bad"} language={language} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RemedyCard({ remedy, language }: { remedy: ActiveRemedy; language: Language }) {
  const copy = COPY[language];
  return (
    <div className="card-sacred p-4">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <p className="text-[15px] font-semibold" style={{ color: "var(--ish-dark)" }}>
          {language === "hi" ? remedy.planet_display_hi : remedy.planet_display_en}
        </p>
        <span className="text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
          {copy.house} {remedy.house} · {remedy.day}
        </span>
      </div>
      {remedy.remedy_summary && (
        <p className="text-[13px] mb-2" style={{ color: "var(--ish-text-muted)" }}>
          {remedy.remedy_summary}
        </p>
      )}
      {remedy.donate.length > 0 && (
        <p className="text-[12px] mt-1" style={{ color: "var(--ish-text-muted)" }}>
          <span className="font-semibold" style={{ color: "var(--ish-accent)" }}>
            {copy.donate}:{" "}
          </span>
          {remedy.donate.join(", ")}
        </p>
      )}
      {remedy.feed && (
        <p className="text-[12px] mt-1" style={{ color: "var(--ish-text-muted)" }}>
          <span className="font-semibold" style={{ color: "var(--ish-accent)" }}>
            {copy.feed}:{" "}
          </span>
          {remedy.feed}
        </p>
      )}
      {remedy.gemstone && (
        <p className="text-[12px] mt-1" style={{ color: "var(--ish-text-muted)" }}>
          <span className="font-semibold" style={{ color: "var(--ish-accent)" }}>
            {copy.gemstone}:{" "}
          </span>
          {remedy.gemstone}
        </p>
      )}
    </div>
  );
}

export function DebtCard({ debt, language }: { debt: DetectedDebt; language: Language }) {
  const copy = COPY[language];
  return (
    <div className="card-sacred p-4 border-l-4" style={{ borderLeftColor: "var(--ish-accent)" }}>
      <p className="text-[14px] font-semibold mb-1" style={{ color: "var(--ish-dark)" }}>
        {debt.name}
      </p>
      <p className="text-[12px] mb-2" style={{ color: "var(--ish-text-faint)" }}>
        {language === "hi" ? debt.planet_display_hi : debt.planet_display_en} · {copy.house}{" "}
        {debt.house}
      </p>
      <p className="text-[13px]" style={{ color: "var(--ish-text-muted)" }}>
        <span className="font-semibold">{copy.remedy}: </span>
        {debt.remedy}
      </p>
    </div>
  );
}

export function ResultPanel({
  result,
  language,
  resultOffer,
}: {
  result: LalKitabKundliResponse;
  language: Language;
  resultOffer: ReactNode;
}) {
  const overall = result.summary.overall;
  const copy = COPY[language];
  const overallLabel = language === "hi" ? HINDI_OVERALL_LABEL[overall] : OVERALL_LABEL[overall];
  return (
    <div className="space-y-8">
      {/* Summary bar */}
      <div className="card-sacred p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p
              className="text-[11px] uppercase tracking-wide font-semibold mb-1"
              style={{ color: "var(--ish-text-faint)" }}
            >
              {copy.assessment}
            </p>
            <p
              className="text-[20px] font-bold"
              style={{ color: OVERALL_COLOR[overall] ?? "var(--ish-dark)" }}
            >
              {overallLabel ?? overall}
            </p>
          </div>
          <div className="flex gap-6 text-center">
            <div>
              <p className="text-[24px] font-bold" style={{ color: "var(--color-success)" }}>
                {result.summary.good_placements}
              </p>
              <p className="text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
                {copy.good}
              </p>
            </div>
            <div>
              <p className="text-[24px] font-bold" style={{ color: "var(--color-error)" }}>
                {result.summary.bad_placements}
              </p>
              <p className="text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
                {copy.needsUpay}
              </p>
            </div>
            <div>
              <p className="text-[24px] font-bold" style={{ color: "var(--ish-accent)" }}>
                {result.summary.total_remedies}
              </p>
              <p className="text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
                {copy.activeUpay}
              </p>
            </div>
          </div>
        </div>
      </div>

      {resultOffer}

      {/* Planet positions table */}
      <div>
        <h2
          className="text-[18px] font-bold mb-3"
          style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
        >
          {copy.planetPlacements}
        </h2>
        <div className="card-sacred p-4">
          <PlanetEffectsTable effects={result.planet_effects} language={language} />
        </div>
      </div>

      {/* Active remedies */}
      {result.active_remedies.length > 0 && (
        <div>
          <h2
            className="text-[18px] font-bold mb-3"
            style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
          >
            {copy.remedies}
          </h2>
          <p className="text-[13px] mb-4" style={{ color: "var(--ish-text-muted)" }}>
            {copy.remediesBody}
          </p>
          <div className="space-y-3">
            {result.active_remedies.map((r) => (
              <RemedyCard key={r.planet} remedy={r} language={language} />
            ))}
          </div>
        </div>
      )}

      {/* Ancestral debts */}
      {result.debts.length > 0 && (
        <div>
          <h2
            className="text-[18px] font-bold mb-3"
            style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
          >
            {copy.debts}
          </h2>
          <p className="text-[13px] mb-4" style={{ color: "var(--ish-text-muted)" }}>
            {copy.debtsBody}
          </p>
          <div className="space-y-3">
            {result.debts.map((d, i) => (
              <DebtCard key={i} debt={d} language={language} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
